import { getAudioContext } from "./audio";

const RECORDING_CHUNK_INTERVAL_MS = 5_000;
const MAX_CHUNK_UPLOAD_ATTEMPTS = 6;
const CHUNK_UPLOAD_TIMEOUT_MS = 20_000;

export type RecordingSource = {
  media: CanvasImageSource;
  name: string;
  visible: boolean;
  fit?: "cover" | "contain";
};

export class MeetingRecorder {
  private recorder: MediaRecorder | undefined;
  private recordingId = "";
  private sequence = 0;
  private uploadQueue: Promise<void> = Promise.resolve();
  private uploadedBytes = 0;
  private uploadFailureNotified = false;
  private renderFrame = 0;
  private recordingStream: MediaStream | undefined;
  private readonly canvas = document.createElement("canvas");
  private readonly context: CanvasRenderingContext2D;
  private readonly audioNodes = new Map<string, MediaStreamAudioSourceNode>();
  private audioDestination: MediaStreamAudioDestinationNode | undefined;
  private lastAudioSyncAt = 0;
  private readonly flushWhenHidden = (): void => {
    if (
      document.visibilityState === "hidden" &&
      this.recorder?.state === "recording"
    ) {
      this.recorder.requestData();
    }
  };

  constructor(
    private readonly roomId: string,
    private readonly token: string,
    private readonly sourceProvider: () => RecordingSource[],
    private readonly audioStreamProvider: () => MediaStream[],
    private readonly onState: (active: boolean, message: string) => void,
    private readonly onProgress: (
      uploadedChunks: number,
      uploadedBytes: number,
    ) => void,
  ) {
    const context = this.canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("녹화 Canvas를 만들 수 없습니다.");
    this.context = context;
    this.canvas.width = 1280;
    this.canvas.height = 720;
  }

  async start(): Promise<string> {
    if (this.recorder) return this.recordingId;
    const mimeType = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ].find((candidate) => MediaRecorder.isTypeSupported(candidate));
    if (!mimeType) throw new Error("이 브라우저는 WebM 녹화를 지원하지 않습니다.");

    const response = await fetch("/api/recordings", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify({ roomId: this.roomId }),
    });
    const payload = (await response.json()) as {
      recordingId?: string;
      error?: string;
    };
    if (!response.ok || !payload.recordingId) {
      throw new Error(payload.error ?? "녹화 세션을 시작하지 못했습니다.");
    }
    this.recordingId = payload.recordingId;
    this.sequence = 0;
    this.uploadedBytes = 0;
    this.uploadFailureNotified = false;
    this.uploadQueue = Promise.resolve();

    const context = getAudioContext();
    this.audioDestination = context.createMediaStreamDestination();
    this.syncAudioSources();

    const canvasStream = this.canvas.captureStream(24);
    const audioTrack = this.audioDestination.stream.getAudioTracks()[0];
    if (audioTrack) canvasStream.addTrack(audioTrack);
    this.recordingStream = canvasStream;
    this.draw = this.draw.bind(this);
    this.renderFrame = requestAnimationFrame(this.draw);

    const recorder = new MediaRecorder(canvasStream, {
      mimeType,
      videoBitsPerSecond: 2_200_000,
      audioBitsPerSecond: 96_000,
    });
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.queueChunk(event.data);
    };
    this.recorder = recorder;
    document.addEventListener("visibilitychange", this.flushWhenHidden);
    recorder.start(RECORDING_CHUNK_INTERVAL_MS);
    this.onState(true, "녹화를 시작했습니다. 5초마다 서버에 저장합니다.");
    return this.recordingId;
  }

  async stop(): Promise<void> {
    const recorder = this.recorder;
    if (!recorder) return;
    await new Promise<void>((resolve) => {
      recorder.addEventListener("stop", () => resolve(), { once: true });
      recorder.stop();
    });
    try {
      await this.uploadQueue;
      const response = await this.fetchWithRetry(
        `/api/recordings/${encodeURIComponent(this.recordingId)}/complete`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${this.token}` },
        },
        "녹화 종료 처리",
      );
      if (!response.ok) {
        const payload = await this.errorPayload(response);
        throw new Error(payload.error ?? "녹화 종료 처리를 완료하지 못했습니다.");
      }
      this.onState(false, "녹화를 안전하게 저장했습니다.");
    } catch (error) {
      this.onState(
        false,
        "녹화가 중단됐지만 업로드된 청크는 서버에서 자동 복구됩니다.",
      );
      throw error;
    } finally {
      this.cleanup();
    }
  }

  flush(): void {
    if (this.recorder?.state === "recording") {
      this.recorder.requestData();
    }
  }

  private queueChunk(blob: Blob): void {
    const sequence = this.sequence;
    this.sequence += 1;
    this.uploadQueue = this.uploadQueue.then(() =>
      this.uploadChunk(blob, sequence),
    );
    void this.uploadQueue.catch(() => {
      if (this.uploadFailureNotified) return;
      this.uploadFailureNotified = true;
      this.onState(
        true,
        "녹화 청크 업로드가 중단됐습니다. 네트워크를 확인한 뒤 녹화를 종료하세요.",
      );
    });
  }

  private async uploadChunk(blob: Blob, sequence: number): Promise<void> {
    const bytes = await blob.arrayBuffer();
    const response = await this.fetchWithRetry(
      `/api/recordings/${encodeURIComponent(this.recordingId)}/chunks/${sequence}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/octet-stream",
          Authorization: `Bearer ${this.token}`,
        },
        body: bytes,
      },
      `${sequence + 1}번 녹화 청크`,
    );
    if (!response.ok) {
      const payload = await this.errorPayload(response);
      throw new Error(payload.error ?? `${sequence + 1}번 녹화 청크 업로드 실패`);
    }
    this.uploadedBytes += bytes.byteLength;
    this.onProgress(sequence + 1, this.uploadedBytes);
  }

  private async fetchWithRetry(
    input: RequestInfo | URL,
    init: RequestInit,
    label: string,
  ): Promise<Response> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= MAX_CHUNK_UPLOAD_ATTEMPTS; attempt += 1) {
      const controller = new AbortController();
      const timeout = window.setTimeout(
        () => controller.abort(),
        CHUNK_UPLOAD_TIMEOUT_MS,
      );
      try {
        const response = await fetch(input, {
          ...init,
          signal: controller.signal,
        });
        if (
          response.ok ||
          (response.status !== 409 &&
            response.status !== 429 &&
            response.status < 500)
        ) {
          return response;
        }
        lastError = new Error(`${label} 업로드 응답: HTTP ${response.status}`);
      } catch (error) {
        lastError = error;
      } finally {
        window.clearTimeout(timeout);
      }
      if (attempt < MAX_CHUNK_UPLOAD_ATTEMPTS) {
        if (attempt === 1) {
          this.onState(true, `${label} 업로드를 다시 시도하고 있습니다.`);
        }
        await new Promise((resolve) =>
          window.setTimeout(resolve, Math.min(500 * 2 ** (attempt - 1), 8_000)),
        );
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new Error(`${label} 업로드에 실패했습니다.`);
  }

  private async errorPayload(response: Response): Promise<{ error?: string }> {
    try {
      return (await response.json()) as { error?: string };
    } catch {
      return {};
    }
  }

  private draw(): void {
    if (performance.now() - this.lastAudioSyncAt > 1_000) {
      this.syncAudioSources();
    }
    const sources = this.sourceProvider().filter((source) => source.visible);
    const count = Math.max(1, Math.min(6, sources.length));
    const columns = count <= 1 ? 1 : count <= 4 ? 2 : 3;
    const rows = Math.ceil(count / columns);
    const cellWidth = this.canvas.width / columns;
    const cellHeight = this.canvas.height / rows;
    this.context.fillStyle = "#071017";
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);

    sources.slice(0, 6).forEach((source, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const x = column * cellWidth;
      const y = row * cellHeight;
      this.drawMedia(
        source.media,
        x,
        y,
        cellWidth,
        cellHeight,
        source.fit ?? "cover",
      );
      this.context.fillStyle = "rgba(4, 10, 14, 0.74)";
      this.context.fillRect(x + 15, y + cellHeight - 44, 180, 29);
      this.context.fillStyle = "#e8f1f3";
      this.context.font = "500 15px sans-serif";
      this.context.fillText(source.name, x + 25, y + cellHeight - 24);
    });
    this.renderFrame = requestAnimationFrame(this.draw);
  }

  private drawMedia(
    source: CanvasImageSource,
    x: number,
    y: number,
    width: number,
    height: number,
    fit: "cover" | "contain",
  ): void {
    const sourceWidth =
      source instanceof HTMLVideoElement
        ? source.videoWidth
        : "width" in source
          ? Number(source.width)
          : width;
    const sourceHeight =
      source instanceof HTMLVideoElement
        ? source.videoHeight
        : "height" in source
          ? Number(source.height)
          : height;
    if (!sourceWidth || !sourceHeight) return;
    const scale = fit === "contain"
      ? Math.min(width / sourceWidth, height / sourceHeight)
      : Math.max(width / sourceWidth, height / sourceHeight);
    const drawWidth = sourceWidth * scale;
    const drawHeight = sourceHeight * scale;
    this.context.drawImage(
      source,
      x + (width - drawWidth) / 2,
      y + (height - drawHeight) / 2,
      drawWidth,
      drawHeight,
    );
  }

  private cleanup(): void {
    document.removeEventListener("visibilitychange", this.flushWhenHidden);
    cancelAnimationFrame(this.renderFrame);
    for (const node of this.audioNodes.values()) node.disconnect();
    this.audioNodes.clear();
    this.audioDestination = undefined;
    for (const track of this.recordingStream?.getTracks() ?? []) track.stop();
    this.recordingStream = undefined;
    this.recorder = undefined;
    this.recordingId = "";
  }

  private syncAudioSources(): void {
    if (!this.audioDestination) return;
    this.lastAudioSyncAt = performance.now();
    const tracks = new Map<string, MediaStreamTrack>();
    for (const stream of this.audioStreamProvider()) {
      for (const track of stream.getAudioTracks()) tracks.set(track.id, track);
    }
    for (const [trackId, node] of this.audioNodes) {
      if (tracks.has(trackId)) continue;
      node.disconnect();
      this.audioNodes.delete(trackId);
    }
    const context = getAudioContext();
    for (const [trackId, track] of tracks) {
      if (this.audioNodes.has(trackId)) continue;
      const source = context.createMediaStreamSource(new MediaStream([track]));
      source.connect(this.audioDestination);
      this.audioNodes.set(trackId, source);
    }
  }
}
